import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import {
  Plus, Trash2, Edit2, Search, Filter,
  Stethoscope, Heart, ShieldCheck,
  CheckCircle2, AlertCircle, Loader2,
  Clock, Calendar, ChevronRight, X,
  HeartHandshake, Layers, IndianRupee,
  Activity, Check, ArrowRight, Tag, Sparkles, FolderPlus,
  Upload, Image as ImageIcon, Globe, FileText,
  HelpCircle, ChevronDown, PlusCircle, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const initialServiceForm = {
  categoryId: '',
  serviceName: '',
  slug: '',
  tagline: '',
  description: '',
  imageUrl: '',
  cardHighlights: [],
  pageTitle: '',
  leadParagraphs: [],
  sectionsIntro: '',
  sections: [],
  whatIs: {
    heading: '',
    paragraphs: []
  },
  whenNeeded: {
    heading: '',
    items: []
  },
  insights: [],
  scopeNotice: {
    title: '',
    badge: '',
    paragraphs: []
  },
  cta: {
    title: '',
    text: ''
  },
  medicalPoints: [],
  nonMedicalPoints: [],
  pricing: {
    pricingMode: 'both',
    perDay: '',
    days7: '',
    days15: '',
    days30: '',
    hours12: '',
    hours24: '',
    customHoursRate: ''
  },
  status: 'active'
};

const OpheServiceManagement = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Category Modal State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', imageUrl: '', status: 'active' });
  const [submittingCategory, setSubmittingCategory] = useState(false);
  const [uploadingCategoryPhoto, setUploadingCategoryPhoto] = useState(false);
  const categoryFileInputRef = useRef(null);

  // Service Modal State
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState(initialServiceForm);
  const [serviceModalTab, setServiceModalTab] = useState('basic'); // 'basic' | 'pricing' | 'content' | 'faq'
  const [submittingService, setSubmittingService] = useState(false);
  const [uploadingServicePhoto, setUploadingServicePhoto] = useState(false);
  const serviceFileInputRef = useRef(null);

  // Input states for adding items
  const [medicalInput, setMedicalInput] = useState('');
  const [nonMedicalInput, setNonMedicalInput] = useState('');
  const [highlightInput, setHighlightInput] = useState('');
  const [leadParagraphInput, setLeadParagraphInput] = useState('');
  const [whatIsParagraphInput, setWhatIsParagraphInput] = useState('');
  const [whenNeededItemInput, setWhenNeededItemInput] = useState('');
  const [scopeParagraphInput, setScopeParagraphInput] = useState('');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newSectionItemInputs, setNewSectionItemInputs] = useState({});
  const [newInsightTitle, setNewInsightTitle] = useState('');
  const [newInsightParagraph, setNewInsightParagraph] = useState('');
  const [newInsightParaInputs, setNewInsightParaInputs] = useState({});

  useEffect(() => {
    fetchCatalog();
  }, []);

  const fetchCatalog = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const [catsRes, servsRes] = await Promise.all([
        api.get('/ophe/categories'),
        api.get('/ophe/services')
      ]);
      setCategories(catsRes.data?.data || []);
      setServices(servsRes.data?.data || []);
    } catch (error) {
      toast.error('Failed to load OPHE catalog data');
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  // Photo Upload Handler
  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, JPEG, WEBP)');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image size must be less than 8MB');
      return;
    }

    const toastId = toast.loading('Uploading photo...');
    if (type === 'category') setUploadingCategoryPhoto(true);
    if (type === 'service') setUploadingServicePhoto(true);

    try {
      const upData = new FormData();
      upData.append('documents', file);

      const response = await api.post('/upload', upData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.data?.files?.[0]?.url) {
        const url = response.data.data.files[0].url;
        if (type === 'category') {
          setCategoryForm(prev => ({ ...prev, imageUrl: url }));
        } else if (type === 'service') {
          setServiceForm(prev => ({ ...prev, imageUrl: url }));
        }
        toast.success('Photo uploaded successfully', { id: toastId });
      } else {
        toast.error('Failed to retrieve uploaded image URL', { id: toastId });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed', { id: toastId });
    } finally {
      if (type === 'category') setUploadingCategoryPhoto(false);
      if (type === 'service') setUploadingServicePhoto(false);
    }
  };

  // Category Actions
  const handleOpenCategoryModal = (cat = null) => {
    if (cat) {
      setEditingCategory(cat);
      setCategoryForm({
        name: cat.name,
        description: cat.description || '',
        imageUrl: cat.imageUrl || '',
        status: cat.status || 'active'
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', imageUrl: '', status: 'active' });
    }
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setSubmittingCategory(true);
    try {
      if (editingCategory) {
        await api.patch(`/ophe/categories/${editingCategory._id}`, categoryForm);
        toast.success('Category updated successfully');
      } else {
        await api.post('/ophe/categories', categoryForm);
        toast.success('Category created successfully');
      }
      setShowCategoryModal(false);
      fetchCatalog(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!isAdmin) {
      toast.error('Only Administrators are authorized to delete categories');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete category "${name}"?`)) return;

    // Optimistic removal: remove category instantly without full page re-render
    setCategories(prev => prev.filter(c => c._id !== id));
    if (selectedCategoryFilter === id) setSelectedCategoryFilter('all');

    try {
      await api.delete(`/ophe/categories/${id}`);
      toast.success('Category deleted successfully');
      fetchCatalog(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete category');
      fetchCatalog(true);
    }
  };

  // Service Actions
  const handleOpenServiceModal = (serv = null) => {
    setServiceModalTab('basic');
    setMedicalInput('');
    setNonMedicalInput('');
    setHighlightInput('');
    setLeadParagraphInput('');
    setWhatIsParagraphInput('');
    setWhenNeededItemInput('');
    setScopeParagraphInput('');
    setNewSectionTitle('');
    setNewSectionItemInputs({});

    if (serv) {
      setEditingService(serv);
      setServiceForm({
        categoryId: serv.categoryId?._id || serv.categoryId || (categories[0]?._id || ''),
        serviceName: serv.serviceName || '',
        slug: serv.slug || '',
        tagline: serv.tagline || '',
        description: serv.description || '',
        imageUrl: serv.imageUrl || '',
        cardHighlights: Array.isArray(serv.cardHighlights) ? [...serv.cardHighlights] : [],
        pageTitle: serv.pageTitle || serv.serviceName || '',
        leadParagraphs: Array.isArray(serv.leadParagraphs) ? [...serv.leadParagraphs] : [],
        sectionsIntro: serv.sectionsIntro || '',
        sections: Array.isArray(serv.sections)
          ? serv.sections.map(s => ({ title: s.title || '', items: Array.isArray(s.items) ? [...s.items] : [] }))
          : [],
        whatIs: {
          heading: serv.whatIs?.heading || '',
          paragraphs: Array.isArray(serv.whatIs?.paragraphs) ? [...serv.whatIs.paragraphs] : []
        },
        whenNeeded: {
          heading: serv.whenNeeded?.heading || '',
          items: Array.isArray(serv.whenNeeded?.items) ? [...serv.whenNeeded.items] : []
        },
        insights: Array.isArray(serv.insights)
          ? serv.insights.map(i => ({
              title: i.title || '',
              paragraphs: Array.isArray(i.paragraphs) ? [...i.paragraphs] : []
            }))
          : [],
        scopeNotice: {
          title: serv.scopeNotice?.title || '',
          badge: serv.scopeNotice?.badge || '',
          paragraphs: Array.isArray(serv.scopeNotice?.paragraphs) ? [...serv.scopeNotice.paragraphs] : []
        },
        cta: {
          title: serv.cta?.title || '',
          text: serv.cta?.text || ''
        },
        medicalPoints: Array.isArray(serv.medicalPoints) ? [...serv.medicalPoints] : [],
        nonMedicalPoints: Array.isArray(serv.nonMedicalPoints) ? [...serv.nonMedicalPoints] : [],
        pricing: {
          pricingMode: serv.pricing?.pricingMode || 'both',
          perDay: serv.pricing?.perDay !== undefined ? serv.pricing.perDay : '',
          days7: serv.pricing?.days7 !== undefined ? serv.pricing.days7 : '',
          days15: serv.pricing?.days15 !== undefined ? serv.pricing.days15 : '',
          days30: serv.pricing?.days30 !== undefined ? serv.pricing.days30 : '',
          hours12: serv.pricing?.hours12 !== undefined ? serv.pricing.hours12 : '',
          hours24: serv.pricing?.hours24 !== undefined ? serv.pricing.hours24 : '',
          customHoursRate: serv.pricing?.customHoursRate !== undefined ? serv.pricing.customHoursRate : ''
        },
        status: serv.status || 'active'
      });
    } else {
      setEditingService(null);
      setServiceForm({
        ...initialServiceForm,
        categoryId: categories.length > 0 ? categories[0]._id : ''
      });
    }
    setShowServiceModal(true);
  };

  // Highlights
  const handleAddHighlight = () => {
    if (!highlightInput.trim()) return;
    if (serviceForm.cardHighlights.includes(highlightInput.trim())) {
      toast.error('This highlight already exists');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      cardHighlights: [...prev.cardHighlights, highlightInput.trim()]
    }));
    setHighlightInput('');
  };

  const handleRemoveHighlight = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      cardHighlights: prev.cardHighlights.filter((_, i) => i !== idx)
    }));
  };

  // Medical Points
  const handleAddMedicalPoint = () => {
    if (!medicalInput.trim()) return;
    if (serviceForm.medicalPoints.includes(medicalInput.trim())) {
      toast.error('This point already exists');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      medicalPoints: [...prev.medicalPoints, medicalInput.trim()]
    }));
    setMedicalInput('');
  };

  const handleRemoveMedicalPoint = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      medicalPoints: prev.medicalPoints.filter((_, i) => i !== idx)
    }));
  };

  // Non-Medical Points
  const handleAddNonMedicalPoint = () => {
    if (!nonMedicalInput.trim()) return;
    if (serviceForm.nonMedicalPoints.includes(nonMedicalInput.trim())) {
      toast.error('This point already exists');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      nonMedicalPoints: [...prev.nonMedicalPoints, nonMedicalInput.trim()]
    }));
    setNonMedicalInput('');
  };

  const handleRemoveNonMedicalPoint = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      nonMedicalPoints: prev.nonMedicalPoints.filter((_, i) => i !== idx)
    }));
  };

  // Lead Paragraphs
  const handleAddLeadParagraph = () => {
    if (!leadParagraphInput.trim()) return;
    setServiceForm(prev => ({
      ...prev,
      leadParagraphs: [...prev.leadParagraphs, leadParagraphInput.trim()]
    }));
    setLeadParagraphInput('');
  };

  const handleRemoveLeadParagraph = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      leadParagraphs: prev.leadParagraphs.filter((_, i) => i !== idx)
    }));
  };

  // Sections (Accordion)
  const handleAddSection = () => {
    if (!newSectionTitle.trim()) {
      toast.error('Enter a section title (e.g. Daily Activities)');
      return;
    }
    setServiceForm(prev => ({
      ...prev,
      sections: [...prev.sections, { title: newSectionTitle.trim(), items: [] }]
    }));
    setNewSectionTitle('');
  };

  const handleRemoveSection = (sIdx) => {
    setServiceForm(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== sIdx)
    }));
  };

  const handleAddItemToSection = (sIdx) => {
    const itemText = (newSectionItemInputs[sIdx] || '').trim();
    if (!itemText) return;
    setServiceForm(prev => {
      const updated = [...prev.sections];
      updated[sIdx] = {
        ...updated[sIdx],
        items: [...(updated[sIdx].items || []), itemText]
      };
      return { ...prev, sections: updated };
    });
    setNewSectionItemInputs(prev => ({ ...prev, [sIdx]: '' }));
  };

  const handleRemoveItemFromSection = (sIdx, itemIdx) => {
    setServiceForm(prev => {
      const updated = [...prev.sections];
      updated[sIdx] = {
        ...updated[sIdx],
        items: updated[sIdx].items.filter((_, i) => i !== itemIdx)
      };
      return { ...prev, sections: updated };
    });
  };

  // What Is Paragraphs
  const handleAddWhatIsParagraph = () => {
    if (!whatIsParagraphInput.trim()) return;
    setServiceForm(prev => ({
      ...prev,
      whatIs: {
        ...prev.whatIs,
        paragraphs: [...(prev.whatIs?.paragraphs || []), whatIsParagraphInput.trim()]
      }
    }));
    setWhatIsParagraphInput('');
  };

  const handleRemoveWhatIsParagraph = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      whatIs: {
        ...prev.whatIs,
        paragraphs: prev.whatIs.paragraphs.filter((_, i) => i !== idx)
      }
    }));
  };

  // When Needed Items
  const handleAddWhenNeededItem = () => {
    if (!whenNeededItemInput.trim()) return;
    setServiceForm(prev => ({
      ...prev,
      whenNeeded: {
        ...prev.whenNeeded,
        items: [...(prev.whenNeeded?.items || []), whenNeededItemInput.trim()]
      }
    }));
    setWhenNeededItemInput('');
  };

  const handleRemoveWhenNeededItem = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      whenNeeded: {
        ...prev.whenNeeded,
        items: prev.whenNeeded.items.filter((_, i) => i !== idx)
      }
    }));
  };

  // Insights Handlers
  const handleAddInsight = () => {
    if (!newInsightTitle.trim()) {
      toast.error('Card title is required (e.g. "Clinical excellence with compassionate empathy")');
      return;
    }
    const newInsight = {
      title: newInsightTitle.trim(),
      paragraphs: newInsightParagraph.trim() ? [newInsightParagraph.trim()] : []
    };
    setServiceForm(prev => ({
      ...prev,
      insights: [...(prev.insights || []), newInsight]
    }));
    setNewInsightTitle('');
    setNewInsightParagraph('');
  };

  const handleRemoveInsight = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      insights: prev.insights.filter((_, i) => i !== idx)
    }));
  };

  const handleUpdateInsightTitle = (idx, title) => {
    setServiceForm(prev => {
      const updated = [...prev.insights];
      updated[idx] = { ...updated[idx], title };
      return { ...prev, insights: updated };
    });
  };

  const handleAddParagraphToInsight = (insightIdx) => {
    const text = (newInsightParaInputs[insightIdx] || '').trim();
    if (!text) return;
    setServiceForm(prev => {
      const updated = [...prev.insights];
      updated[insightIdx] = {
        ...updated[insightIdx],
        paragraphs: [...(updated[insightIdx].paragraphs || []), text]
      };
      return { ...prev, insights: updated };
    });
    setNewInsightParaInputs(prev => ({ ...prev, [insightIdx]: '' }));
  };

  const handleUpdateInsightParagraph = (insightIdx, paraIdx, text) => {
    setServiceForm(prev => {
      const updated = [...prev.insights];
      const updatedParas = [...(updated[insightIdx]?.paragraphs || [])];
      updatedParas[paraIdx] = text;
      updated[insightIdx] = { ...updated[insightIdx], paragraphs: updatedParas };
      return { ...prev, insights: updated };
    });
  };

  const handleRemoveParagraphFromInsight = (insightIdx, paraIdx) => {
    setServiceForm(prev => {
      const updated = [...prev.insights];
      updated[insightIdx] = {
        ...updated[insightIdx],
        paragraphs: (updated[insightIdx]?.paragraphs || []).filter((_, i) => i !== paraIdx)
      };
      return { ...prev, insights: updated };
    });
  };

  // Scope Notice Paragraphs
  const handleAddScopeParagraph = () => {
    if (!scopeParagraphInput.trim()) return;
    setServiceForm(prev => ({
      ...prev,
      scopeNotice: {
        ...prev.scopeNotice,
        paragraphs: [...(prev.scopeNotice?.paragraphs || []), scopeParagraphInput.trim()]
      }
    }));
    setScopeParagraphInput('');
  };

  const handleRemoveScopeParagraph = (idx) => {
    setServiceForm(prev => ({
      ...prev,
      scopeNotice: {
        ...prev.scopeNotice,
        paragraphs: prev.scopeNotice.paragraphs.filter((_, i) => i !== idx)
      }
    }));
  };

  // Save Service
  const handleSaveService = async (e) => {
    e.preventDefault();
    if (!serviceForm.categoryId) {
      toast.error('Please select a category');
      return;
    }
    if (!serviceForm.serviceName.trim()) {
      toast.error('Service name is required');
      return;
    }

    setSubmittingService(true);
    try {
      const payload = {
        ...serviceForm,
        slug: serviceForm.slug?.trim()
          ? serviceForm.slug.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
          : serviceForm.serviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
        pricing: {
          pricingMode: serviceForm.pricing.pricingMode || 'both',
          perDay: Number(serviceForm.pricing.perDay) || 0,
          days7: Number(serviceForm.pricing.days7) || 0,
          days15: Number(serviceForm.pricing.days15) || 0,
          days30: Number(serviceForm.pricing.days30) || 0,
          hours12: Number(serviceForm.pricing.hours12) || 0,
          hours24: Number(serviceForm.pricing.hours24) || 0,
          customHoursRate: Number(serviceForm.pricing.customHoursRate) || 0
        }
      };

      if (editingService) {
        await api.patch(`/ophe/services/${editingService._id}`, payload);
        toast.success('OPHE Service updated successfully');
      } else {
        await api.post('/ophe/services', payload);
        toast.success('OPHE Service created successfully');
      }
      setShowServiceModal(false);
      fetchCatalog(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save OPHE service');
    } finally {
      setSubmittingService(false);
    }
  };

  const handleDeleteService = async (id, name) => {
    if (!isAdmin) {
      toast.error('Only Administrators are authorized to delete services');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete service "${name}"?`)) return;

    // Optimistic removal: immediately remove service from list for instant UI feedback without full page refresh
    setServices(prev => prev.filter(s => s._id !== id));

    try {
      await api.delete(`/ophe/services/${id}`);
      toast.success('Service deleted successfully');
      fetchCatalog(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete service');
      fetchCatalog(true);
    }
  };

  // Filtered Services
  const filteredServices = services.filter(serv => {
    const matchesCategory = selectedCategoryFilter === 'all' ||
      (serv.categoryId?._id || serv.categoryId) === selectedCategoryFilter;
    const matchesSearch = !searchQuery ||
      serv.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      serv.slug?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      serv.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      serv.medicalPoints?.some(p => p.toLowerCase().includes(searchQuery.toLowerCase())) ||
      serv.nonMedicalPoints?.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-teal-800 via-teal-700 to-cyan-800 rounded-2xl p-6 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight">OPHE Services & Package Management</h1>
          <p className="text-teal-100/90 text-xs max-w-2xl mt-1 leading-relaxed">
            Manage OPHE categories, website cards, detail page content, accordion checklists, and duration pricing. Changes reflect live on the OPHE patient website.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => handleOpenCategoryModal()}
            className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-2 border border-white/20 backdrop-blur-xs cursor-pointer shadow-sm"
          >
            <FolderPlus className="w-4 h-4" />
            <span>Manage Categories</span>
          </button>
          <button
            onClick={() => handleOpenServiceModal()}
            className="px-4 py-2.5 bg-white text-teal-900 hover:bg-teal-50 rounded-xl text-xs font-black transition-all flex items-center gap-2 shadow-lg cursor-pointer"
          >
            <Plus className="w-4 h-4 text-teal-700" />
            <span>+ New Service Package</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200/90 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search services, slugs, descriptions, or inclusions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-600 focus:bg-white transition-all font-medium"
            />
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={selectedCategoryFilter}
              onChange={(e) => setSelectedCategoryFilter(e.target.value)}
              className="px-3 py-2 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-600 cursor-pointer"
            >
              <option value="all">All Categories ({services.length})</option>
              {categories.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-xs font-bold text-slate-500 flex items-center gap-1.5 self-end sm:self-center">
          <Layers className="w-3.5 h-3.5 text-teal-600" />
          <span>Showing <strong className="text-slate-900">{filteredServices.length}</strong> of {services.length} services</span>
        </div>
      </div>

      {/* Quick Category Pills */}
      {categories.length > 0 && (
        <details className="bg-slate-50/80 p-3.5 rounded-2xl border border-slate-200/70 group">
          <summary className="text-xs font-bold text-slate-700 flex items-center justify-between cursor-pointer list-none">
            <span className="flex items-center gap-1.5">
              <FolderPlus className="w-3.5 h-3.5 text-teal-600" />
              Categories Overview ({categories.length})
            </span>
            <span className="text-[10px] text-teal-700 font-bold group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5 mt-3 pt-3 border-t border-slate-200/60">
            {categories.map(cat => (
              <div key={cat._id} className="bg-white p-2.5 rounded-xl border border-slate-200 flex items-center justify-between text-xs shadow-2xs">
                <div className="flex items-center gap-2 truncate">
                  {cat.imageUrl ? (
                    <img src={cat.imageUrl} alt={cat.name} className="w-7 h-7 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center font-bold shrink-0 text-[10px]">
                      {cat.name.charAt(0)}
                    </div>
                  )}
                  <span className="font-bold text-slate-800 truncate">{cat.name}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleOpenCategoryModal(cat)}
                    className="p-1.5 text-slate-500 hover:text-teal-700 hover:bg-white rounded-lg transition-colors cursor-pointer"
                    title="Edit Category"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteCategory(cat._id, cat.name);
                      }}
                      className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-white rounded-lg transition-colors cursor-pointer"
                      title="Delete Category (Admin only)"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* SERVICES LIST */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px] bg-white rounded-2xl border border-slate-200">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
            <span className="text-xs font-bold text-slate-500">Loading OPHE Services Catalog...</span>
          </div>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200 p-6">
          <HeartHandshake className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-800">No OPHE Services Found</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            {categories.length === 0
              ? 'Start by creating your first OPHE Category, then add service packages with photos, website content, and duration pricing.'
              : 'No service packages match your filter. Click "+ New Service Package" to create one.'}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            {categories.length === 0 ? (
              <button
                onClick={() => handleOpenCategoryModal()}
                className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer"
              >
                Create Category First
              </button>
            ) : (
              <button
                onClick={() => handleOpenServiceModal()}
                className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer"
              >
                + Add Service Package
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredServices.map(serv => (
            <div
              key={serv._id}
              className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all flex flex-col justify-between overflow-hidden group"
            >
              {/* Photo Banner Header */}
              {serv.imageUrl ? (
                <div className="h-44 w-full overflow-hidden bg-slate-100 relative">
                  <img
                    src={serv.imageUrl}
                    alt={serv.serviceName}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-white">
                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-white/90 text-teal-900 backdrop-blur-xs">
                      {serv.categoryId?.name || 'Category'}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${serv.status === 'active' ? 'bg-emerald-500 text-white' : 'bg-slate-500 text-white'
                      }`}>
                      {serv.status}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Card Header Body */}
              <div className="p-5 border-b border-slate-100 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {!serv.imageUrl && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-teal-50 text-teal-800 border border-teal-200 mb-1.5">
                        {serv.categoryId?.name || 'General Category'}
                      </span>
                    )}
                    <h3 className="text-base font-black text-slate-900">{serv.serviceName}</h3>
                    {serv.slug && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Globe className="w-3 h-3 text-teal-600" />
                        <span className="text-[10px] text-teal-800 font-mono font-bold bg-teal-50 px-1.5 py-0.5 rounded border border-teal-200/60">
                          /services?service={serv.slug}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleOpenServiceModal(serv)}
                      className="p-1.5 text-slate-500 hover:text-teal-700 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                      title="Edit Service"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteService(serv._id, serv.serviceName);
                        }}
                        className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
                        title="Delete Service (Admin only)"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {serv.description && (
                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                    {serv.description}
                  </p>
                )}

                {/* Website Highlights Preview */}
                {serv.cardHighlights && serv.cardHighlights.length > 0 && (
                  <div className="pt-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                      Website Card Highlights ({serv.cardHighlights.length})
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {serv.cardHighlights.map((hl, i) => (
                        <span key={i} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-medium flex items-center gap-1">
                          <Check className="w-3 h-3 text-sky-600" />
                          {hl}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sections Accordion Count Tag */}
              <div className="px-5 py-2.5 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-600 font-semibold flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                  Website Content: <strong className="text-slate-900">{serv.sections?.length || 0} Accordion Sections</strong>
                </span>
                {serv.whatIs?.heading && (
                  <span className="text-[10px] font-bold text-teal-800 bg-teal-100 px-2 py-0.5 rounded-full">
                    FAQ Configured
                  </span>
                )}
              </div>

              {/* DURATION PRICING RIBBON */}
              <div className="bg-slate-50/80 p-4 border-t border-slate-100 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <IndianRupee className="w-3 h-3 text-teal-600" />
                    Duration & Shift Pricing Plans
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/70 text-slate-700 capitalize">
                    {serv.pricing?.pricingMode === 'hourly_shift' ? 'Hourly / Shift' : serv.pricing?.pricingMode === 'per_day' ? 'Per Day / Multi-Day' : 'Daily & Shift'}
                  </span>
                </div>

                {/* Shift / Hourly Rates */}
                {(serv.pricing?.pricingMode !== 'per_day' || serv.pricing?.hours12 || serv.pricing?.hours24) && (
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="bg-white p-1.5 rounded-lg border border-teal-200/60 shadow-2xs">
                      <span className="text-[9px] text-teal-700 block font-bold">12h Shift</span>
                      <span className="text-xs font-black text-slate-900">
                        ₹{serv.pricing?.hours12?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-indigo-200/60 shadow-2xs">
                      <span className="text-[9px] text-indigo-700 block font-bold">24h Shift</span>
                      <span className="text-xs font-black text-indigo-950">
                        ₹{serv.pricing?.hours24?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">Custom/Hr</span>
                      <span className="text-xs font-black text-slate-800">
                        ₹{serv.pricing?.customHoursRate?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Daily & Multi-Day Rates */}
                {(serv.pricing?.pricingMode !== 'hourly_shift' || serv.pricing?.perDay || serv.pricing?.days7) && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-center">
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">Per Day</span>
                      <span className="text-xs font-black text-slate-900">
                        ₹{serv.pricing?.perDay?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">7 Days</span>
                      <span className="text-xs font-black text-teal-800">
                        ₹{serv.pricing?.days7?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">15 Days</span>
                      <span className="text-xs font-black text-blue-800">
                        ₹{serv.pricing?.days15?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                    <div className="bg-white p-1.5 rounded-lg border border-slate-200/80 shadow-2xs">
                      <span className="text-[9px] text-slate-500 block font-bold">30 Days</span>
                      <span className="text-xs font-black text-emerald-800">
                        ₹{serv.pricing?.days30?.toLocaleString('en-IN') || '0'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL 1: CATEGORY CREATE / EDIT */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-teal-600" />
                <h3 className="text-sm font-black text-slate-900">
                  {editingCategory ? 'Edit Category' : 'Create OPHE Category'}
                </h3>
              </div>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">
                  Category Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Home Care, Nursing Care, Elderly Care"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                />
              </div>

              {/* Photo Upload */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 block">Category Icon / Photo</label>
                <div className="flex items-center gap-3">
                  {categoryForm.imageUrl ? (
                    <div className="relative group w-14 h-14 rounded-xl overflow-hidden border border-slate-200 shrink-0">
                      <img src={categoryForm.imageUrl} alt="Category" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setCategoryForm({ ...categoryForm, imageUrl: '' })}
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon className="w-5 h-5" />
                    </div>
                  )}

                  <div className="flex-1 space-y-1">
                    <input
                      type="file"
                      ref={categoryFileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handlePhotoUpload(e, 'category')}
                    />
                    <button
                      type="button"
                      disabled={uploadingCategoryPhoto}
                      onClick={() => categoryFileInputRef.current?.click()}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      {uploadingCategoryPhoto ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5 text-slate-600" />
                      )}
                      <span>{categoryForm.imageUrl ? 'Change Photo' : 'Upload Photo'}</span>
                    </button>
                    <input
                      type="text"
                      placeholder="Or paste image URL"
                      value={categoryForm.imageUrl}
                      onChange={(e) => setCategoryForm({ ...categoryForm, imageUrl: e.target.value })}
                      className="w-full px-2.5 py-1 text-[11px] border border-slate-200 rounded-lg focus:outline-none focus:border-teal-600"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Description (Optional)</label>
                <textarea
                  rows="2"
                  placeholder="Brief description of services falling under this category..."
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Status</label>
                <select
                  value={categoryForm.status}
                  onChange={(e) => setCategoryForm({ ...categoryForm, status: e.target.value })}
                  className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCategory || uploadingCategoryPhoto}
                  className="px-4 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer shadow-sm"
                >
                  {submittingCategory && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  <span>{editingCategory ? 'Update Category' : 'Save Category'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: COMPREHENSIVE OPHE SERVICE CREATE / EDIT MODAL */}
      {showServiceModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150 my-6 flex flex-col max-h-[90vh]">

            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0 bg-slate-50/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center shrink-0">
                  <HeartHandshake className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    {editingService ? `Edit Service: ${editingService.serviceName}` : 'Create OPHE Service Package'}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Configure catalog card, detail page content, accordion checklists, and duration pricing
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowServiceModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Navigation Tabs */}
            <div className="px-6 pt-3 bg-white border-b border-slate-200/80 flex items-center gap-2 overflow-x-auto no-scrollbar shrink-0">
              <button
                type="button"
                onClick={() => setServiceModalTab('basic')}
                className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${serviceModalTab === 'basic'
                    ? 'border-teal-700 text-teal-800 bg-teal-50/50 rounded-t-lg'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <ImageIcon className="w-3.5 h-3.5" />
                <span>1. Basic & Card</span>
              </button>

              <button
                type="button"
                onClick={() => setServiceModalTab('pricing')}
                className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${serviceModalTab === 'pricing'
                    ? 'border-teal-700 text-teal-800 bg-teal-50/50 rounded-t-lg'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <IndianRupee className="w-3.5 h-3.5" />
                <span>2. Duration Pricing</span>
              </button>

              <button
                type="button"
                onClick={() => setServiceModalTab('content')}
                className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${serviceModalTab === 'content'
                    ? 'border-teal-700 text-teal-800 bg-teal-50/50 rounded-t-lg'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>3. Detail Page & Accordion ({serviceForm.sections?.length || 0})</span>
              </button>

              <button
                type="button"
                onClick={() => setServiceModalTab('faq')}
                className={`px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap ${serviceModalTab === 'faq'
                    ? 'border-teal-700 text-teal-800 bg-teal-50/50 rounded-t-lg'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <HelpCircle className="w-3.5 h-3.5" />
                <span>4. FAQ, Notes & CTA</span>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveService} className="flex-1 overflow-y-auto p-6 space-y-6">

              {/* =========================================================
                  TAB 1: BASIC & CARD
                 ========================================================= */}
              {serviceModalTab === 'basic' && (
                <div className="space-y-5 animate-in fade-in duration-150">
                  {/* Category & Service Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Select Category <span className="text-rose-500">*</span>
                      </label>
                      <select
                        required
                        value={serviceForm.categoryId}
                        onChange={(e) => setServiceForm({ ...serviceForm, categoryId: e.target.value })}
                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-teal-600"
                      >
                        <option value="">-- Choose Category --</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Service Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Basic Home Care"
                        value={serviceForm.serviceName}
                        onChange={(e) => {
                          const name = e.target.value;
                          setServiceForm(prev => ({
                            ...prev,
                            serviceName: name,
                            // auto-generate slug if empty or matching previous name
                            slug: prev.slug ? prev.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
                          }));
                        }}
                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                      />
                    </div>
                  </div>

                  {/* URL Slug & Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 flex items-center justify-between mb-1">
                        <span className="flex items-center gap-1">
                          <Globe className="w-3.5 h-3.5 text-teal-600" />
                          Website URL Slug
                        </span>
                        <span className="text-[10px] text-slate-400 font-normal">URL key on /services?service=slug</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. basic-home-care"
                        value={serviceForm.slug}
                        onChange={(e) => setServiceForm({
                          ...serviceForm,
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
                        })}
                        className="w-full px-3.5 py-2 font-mono text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-teal-600 bg-slate-50/50"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Package Status</label>
                      <select
                        value={serviceForm.status}
                        onChange={(e) => setServiceForm({ ...serviceForm, status: e.target.value })}
                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                      >
                        <option value="active">Active (Visible on Website)</option>
                        <option value="inactive">Inactive (Hidden)</option>
                      </select>
                    </div>
                  </div>

                  {/* Service Photo / Banner Image */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-teal-600" />
                      Service Photo / Banner Image (Website Catalog & Detail Page)
                    </label>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      {serviceForm.imageUrl ? (
                        <div className="relative group w-36 h-24 rounded-xl overflow-hidden border border-slate-200 shrink-0">
                          <img src={serviceForm.imageUrl} alt="Service Preview" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setServiceForm({ ...serviceForm, imageUrl: '' })}
                            className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            title="Remove photo"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-36 h-24 rounded-xl bg-white border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 shrink-0">
                          <ImageIcon className="w-6 h-6 mb-1 text-slate-300" />
                          <span className="text-[10px] font-bold">No Photo</span>
                        </div>
                      )}

                      <div className="flex-1 space-y-1.5 w-full">
                        <input
                          type="file"
                          ref={serviceFileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(e, 'service')}
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={uploadingServicePhoto}
                            onClick={() => serviceFileInputRef.current?.click()}
                            className="px-3.5 py-1.5 bg-white hover:bg-slate-100 border border-slate-300 text-slate-800 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-2xs"
                          >
                            {uploadingServicePhoto ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Upload className="w-3.5 h-3.5 text-teal-600" />
                            )}
                            <span>{serviceForm.imageUrl ? 'Change Photo' : 'Upload Service Photo'}</span>
                          </button>
                          <span className="text-[10px] text-slate-400">PNG, JPG, WEBP (Max 8MB)</span>
                        </div>
                        <input
                          type="text"
                          placeholder="Or paste external image URL / path (e.g. /assets/image2.jpg)"
                          value={serviceForm.imageUrl}
                          onChange={(e) => setServiceForm({ ...serviceForm, imageUrl: e.target.value })}
                          className="w-full px-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-teal-600 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Description */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      Catalog Card Description (Shown on Home Catalog Card)
                    </label>
                    <textarea
                      rows="2"
                      placeholder="e.g. Caregiver help with daily activities and personal care to stay comfortable at home."
                      value={serviceForm.description}
                      onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                      className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                    />
                  </div>

                  {/* Card Key Highlights (The 3 bullet points on the Catalog Card) */}
                  <div className="p-4 bg-sky-50/60 rounded-xl border border-sky-200/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-xs font-black uppercase tracking-wider text-sky-900 flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-sky-600" />
                          Website Card Highlights ({serviceForm.cardHighlights.length})
                        </label>
                        <p className="text-[11px] text-sky-800/80">
                          The bullet points with blue checkmarks displayed on the homepage catalog card
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g. Personal care & daily routine"
                        value={highlightInput}
                        onChange={(e) => setHighlightInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddHighlight(); } }}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-sky-200 rounded-xl text-xs font-medium focus:outline-none focus:border-sky-600"
                      />
                      <button
                        type="button"
                        onClick={handleAddHighlight}
                        className="px-3.5 py-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0"
                      >
                        + Add Highlight
                      </button>
                    </div>

                    {serviceForm.cardHighlights.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        {serviceForm.cardHighlights.map((hl, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-sky-200/70 text-xs">
                            <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                              <Check className="w-3.5 h-3.5 text-sky-600" />
                              {hl}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveHighlight(idx)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* =========================================================
                  TAB 2: DURATION PRICING (EXACT SAME UI IN SCREENSHOT 1)
                 ========================================================= */}
              {serviceModalTab === 'pricing' && (
                <div className="space-y-5 animate-in fade-in duration-150">
                  {/* Pricing Configuration Card */}
                  <div className="p-4 bg-slate-50/90 rounded-2xl border border-slate-200/80 space-y-4 shadow-2xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
                      <div>
                        <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                          <IndianRupee className="w-4 h-4 text-emerald-600" />
                          Duration Pricing Configuration
                        </label>
                        <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                          Choose pricing model: Per Day rates, Shift/Hourly rates (12h & 24h default + custom), or both.
                        </p>
                      </div>

                      {/* Mode Selector Tabs */}
                      <div className="inline-flex p-1 bg-white rounded-xl border border-slate-200/80 shadow-2xs shrink-0">
                        <button
                          type="button"
                          onClick={() => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, pricingMode: 'per_day' }
                          })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${serviceForm.pricing.pricingMode === 'per_day'
                              ? 'bg-teal-700 text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        >
                          Per Day
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, pricingMode: 'hourly_shift' }
                          })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${serviceForm.pricing.pricingMode === 'hourly_shift'
                              ? 'bg-teal-700 text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        >
                          Hourly / Shift
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceForm({
                            ...serviceForm,
                            pricing: { ...serviceForm.pricing, pricingMode: 'both' }
                          })}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${serviceForm.pricing.pricingMode === 'both'
                              ? 'bg-teal-700 text-white shadow-xs'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                            }`}
                        >
                          Both Options
                        </button>
                      </div>
                    </div>

                    {/* Shift & Hourly Pricing (12h, 24h, Custom) */}
                    {(serviceForm.pricing.pricingMode === 'hourly_shift' || serviceForm.pricing.pricingMode === 'both') && (
                      <div className="space-y-2">
                        <span className="text-[11px] font-black uppercase tracking-wider text-teal-800 flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-teal-600" />
                          Shift & Hourly Rates (12h & 24h Shifts + Custom)
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="bg-white p-3 rounded-xl border border-teal-200/70 shadow-2xs">
                            <label className="text-[11px] font-bold text-teal-900 block mb-1">
                              12 Hours Shift Rate (₹) <span className="text-[10px] text-teal-600 font-normal">(Default 12h)</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 800"
                              value={serviceForm.pricing.hours12}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, hours12: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                          <div className="bg-white p-3 rounded-xl border border-indigo-200/70 shadow-2xs">
                            <label className="text-[11px] font-bold text-indigo-900 block mb-1">
                              24 Hours Shift Rate (₹) <span className="text-[10px] text-indigo-600 font-normal">(Default 24h)</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 1500"
                              value={serviceForm.pricing.hours24}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, hours24: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-indigo-950 focus:outline-none focus:border-indigo-600 focus:bg-white"
                            />
                          </div>
                          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                            <label className="text-[11px] font-bold text-slate-700 block mb-1">
                              Custom Shift / Per Hour (₹) <span className="text-[10px] text-slate-400 font-normal">(Custom Rate)</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 100"
                              value={serviceForm.pricing.customHoursRate}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, customHoursRate: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Daily & Multi-Day Package Rates */}
                    {(serviceForm.pricing.pricingMode === 'per_day' || serviceForm.pricing.pricingMode === 'both') && (
                      <div className="space-y-2">
                        <span className="text-[11px] font-black uppercase tracking-wider text-slate-700 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-slate-600" />
                          Daily & Multi-Day Package Rates
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                            <label className="text-[11px] font-bold text-slate-600 block mb-1">Per Day (₹)</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 1200"
                              value={serviceForm.pricing.perDay}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, perDay: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                            <label className="text-[11px] font-bold text-teal-800 block mb-1">7 Days (₹)</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 7500"
                              value={serviceForm.pricing.days7}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, days7: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-teal-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                            <label className="text-[11px] font-bold text-blue-800 block mb-1">15 Days (₹)</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 14000"
                              value={serviceForm.pricing.days15}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, days15: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-blue-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                          <div className="bg-white p-3 rounded-xl border border-slate-200/80 shadow-2xs">
                            <label className="text-[11px] font-bold text-emerald-800 block mb-1">30 Days (₹)</label>
                            <input
                              type="number"
                              min="0"
                              placeholder="e.g. 25000"
                              value={serviceForm.pricing.days30}
                              onChange={(e) => setServiceForm({
                                ...serviceForm,
                                pricing: { ...serviceForm.pricing, days30: e.target.value }
                              })}
                              className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-emerald-800 focus:outline-none focus:border-teal-600 focus:bg-white"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Medical Inclusions Points */}
                  <div className="p-4 bg-teal-50/50 rounded-xl border border-teal-200/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-black uppercase tracking-wider text-teal-900 flex items-center gap-1.5">
                        <Stethoscope className="w-4 h-4 text-teal-700" />
                        Medical Inclusions (Medical Points)
                      </label>
                      <span className="text-[10px] font-bold text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full">
                        {serviceForm.medicalPoints.length} added
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g., Vital monitoring (BP, Sugar, Pulse), Medication administration"
                        value={medicalInput}
                        onChange={(e) => setMedicalInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddMedicalPoint(); } }}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-teal-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-700"
                      />
                      <button
                        type="button"
                        onClick={handleAddMedicalPoint}
                        className="px-3 py-1.5 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 transition-all cursor-pointer shrink-0"
                      >
                        + Add
                      </button>
                    </div>

                    {serviceForm.medicalPoints.length > 0 && (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto no-scrollbar pt-1">
                        {serviceForm.medicalPoints.map((pt, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-teal-200/70 text-xs">
                            <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                              <Check className="w-3.5 h-3.5 text-teal-600" />
                              {pt}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveMedicalPoint(idx)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Non-Medical Inclusions Points */}
                  <div className="p-4 bg-violet-50/50 rounded-xl border border-violet-200/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-black uppercase tracking-wider text-violet-900 flex items-center gap-1.5">
                        <Heart className="w-4 h-4 text-violet-700" />
                        Non-Medical Inclusions (Care & Attendant Points)
                      </label>
                      <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">
                        {serviceForm.nonMedicalPoints.length} added
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="e.g., Personal hygiene & bathing, Feeding assistance, Mobility support"
                        value={nonMedicalInput}
                        onChange={(e) => setNonMedicalInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNonMedicalPoint(); } }}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-violet-200 rounded-xl text-xs font-medium focus:outline-none focus:border-violet-700"
                      />
                      <button
                        type="button"
                        onClick={handleAddNonMedicalPoint}
                        className="px-3 py-1.5 bg-violet-700 text-white rounded-xl text-xs font-bold hover:bg-violet-800 transition-all cursor-pointer shrink-0"
                      >
                        + Add
                      </button>
                    </div>

                    {serviceForm.nonMedicalPoints.length > 0 && (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto no-scrollbar pt-1">
                        {serviceForm.nonMedicalPoints.map((pt, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white px-3 py-1.5 rounded-lg border border-violet-200/70 text-xs">
                            <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                              <Check className="w-3.5 h-3.5 text-violet-600" />
                              {pt}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveNonMedicalPoint(idx)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* =========================================================
                  TAB 3: DETAIL PAGE & ACCORDION
                 ========================================================= */}
              {serviceModalTab === 'content' && (
                <div className="space-y-5 animate-in fade-in duration-150">
                  {/* Page Title & Tagline */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Detail Page Main Title (H1)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Basic Home Care at Home"
                        value={serviceForm.pageTitle}
                        onChange={(e) => setServiceForm({ ...serviceForm, pageTitle: e.target.value })}
                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-teal-600"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">
                        Detail Page Tagline (Subheading)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Everyday support to help your loved one live comfortably and safely at home."
                        value={serviceForm.tagline}
                        onChange={(e) => setServiceForm({ ...serviceForm, tagline: e.target.value })}
                        className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600"
                      />
                    </div>
                  </div>

                  {/* Lead Paragraphs Builder */}
                  <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200 space-y-3">
                    <label className="text-xs font-black uppercase tracking-wider text-slate-800 block">
                      Lead Paragraphs (Introduction Overview)
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        rows="2"
                        placeholder="Add introductory paragraph explaining this service..."
                        value={leadParagraphInput}
                        onChange={(e) => setLeadParagraphInput(e.target.value)}
                        className="flex-1 px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddLeadParagraph}
                        className="px-4 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center"
                      >
                        + Add Paragraph
                      </button>
                    </div>

                    {serviceForm.leadParagraphs.length > 0 && (
                      <div className="space-y-2 pt-1">
                        {serviceForm.leadParagraphs.map((para, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-xl border border-slate-200 text-xs text-slate-700 flex items-start justify-between gap-3 shadow-2xs">
                            <p className="flex-1 leading-relaxed">{para}</p>
                            <button
                              type="button"
                              onClick={() => handleRemoveLeadParagraph(idx)}
                              className="text-slate-400 hover:text-rose-600 p-1 cursor-pointer shrink-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* "What's Included In Our Care" Intro */}
                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">
                      whats included in our care
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. OPHE helps families identify the appropriate type of caregiver support..."
                      value={serviceForm.sectionsIntro}
                      onChange={(e) => setServiceForm({ ...serviceForm, sectionsIntro: e.target.value })}
                      className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600"
                    />
                  </div>

                  {/* ACCORDION SECTIONS BUILDER */}
                  <div className="p-4 bg-teal-50/40 rounded-xl border border-teal-200/80 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-xs font-black uppercase tracking-wider text-teal-900 flex items-center gap-1.5">
                          <Sparkles className="w-4 h-4 text-teal-700" />
                          Interactive Care Accordion Sections ({serviceForm.sections.length})
                        </label>
                        <p className="text-[11px] text-teal-800">
                          These display as expandable accordion drawers containing care tasks and checklists on the website
                        </p>
                      </div>
                    </div>

                    {/* Add New Section Drawer */}
                    <div className="flex gap-2 bg-white p-2.5 rounded-xl border border-teal-200 shadow-2xs">
                      <input
                        type="text"
                        placeholder="New section title (e.g. Daily Activities, Personal Care)..."
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSection(); } }}
                        className="flex-1 px-3 py-1.5 text-xs font-semibold text-slate-900 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddSection}
                        className="px-4 py-1.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0"
                      >
                        + Create Section
                      </button>
                    </div>

                    {/* List of Sections with their Items */}
                    {serviceForm.sections.length > 0 && (
                      <div className="space-y-3 pt-1">
                        {serviceForm.sections.map((sec, sIdx) => (
                          <div key={sIdx} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                            <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
                              <span className="text-xs font-black text-slate-900 flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-teal-600" />
                                {sec.title}
                                <span className="text-[10px] text-slate-400 font-normal">({sec.items?.length || 0} items)</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSection(sIdx)}
                                className="text-rose-500 hover:text-rose-700 text-[11px] font-bold flex items-center gap-1 cursor-pointer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Remove Section</span>
                              </button>
                            </div>

                            <div className="p-4 space-y-3">
                              {/* Add Item into this section */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder={`Add care task under "${sec.title}"...`}
                                  value={newSectionItemInputs[sIdx] || ''}
                                  onChange={(e) => setNewSectionItemInputs({ ...newSectionItemInputs, [sIdx]: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItemToSection(sIdx); } }}
                                  className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:border-teal-600 focus:bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleAddItemToSection(sIdx)}
                                  className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0"
                                >
                                  + Add Item
                                </button>
                              </div>

                              {/* Items list */}
                              {sec.items && sec.items.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                                  {sec.items.map((it, itIdx) => (
                                    <div key={itIdx} className="flex items-center justify-between bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200/70 text-xs">
                                      <span className="font-medium text-slate-800 flex items-center gap-1.5 truncate">
                                        <Check className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                                        <span className="truncate">{it}</span>
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveItemFromSection(sIdx, itIdx)}
                                        className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer shrink-0 ml-1"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* =========================================================
                  TAB 4: FAQ, SCOPE NOTICE & CTA
                 ========================================================= */}
              {serviceModalTab === 'faq' && (
                <div className="space-y-5 animate-in fade-in duration-150">
                  {/* 1. What is this Service? */}
                  <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200 space-y-3">
                    <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <HelpCircle className="w-4 h-4 text-teal-600" />
                      1. "What is this Service?" Block
                    </label>
                    <input
                      type="text"
                      placeholder="Heading (e.g. What is Basic Home Care?)"
                      value={serviceForm.whatIs.heading}
                      onChange={(e) => setServiceForm({
                        ...serviceForm,
                        whatIs: { ...serviceForm.whatIs, heading: e.target.value }
                      })}
                      className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-teal-600"
                    />

                    <div className="flex gap-2">
                      <textarea
                        rows="2"
                        placeholder="Add explanation paragraph for this block..."
                        value={whatIsParagraphInput}
                        onChange={(e) => setWhatIsParagraphInput(e.target.value)}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-teal-600 resize-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddWhatIsParagraph}
                        className="px-3.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center"
                      >
                        + Add
                      </button>
                    </div>

                    {serviceForm.whatIs.paragraphs?.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        {serviceForm.whatIs.paragraphs.map((p, idx) => (
                          <div key={idx} className="bg-white p-2.5 rounded-lg border border-slate-200 text-xs text-slate-700 flex items-start justify-between gap-2 shadow-2xs">
                            <p className="flex-1 leading-relaxed">{p}</p>
                            <button
                              type="button"
                              onClick={() => handleRemoveWhatIsParagraph(idx)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 2. When might you need this Service? */}
                  <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200 space-y-3">
                    <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                      <HelpCircle className="w-4 h-4 text-indigo-600" />
                      2. "When might you need this Service?" Block
                    </label>
                    <input
                      type="text"
                      placeholder="Heading (e.g. When might you need Basic Home Care?)"
                      value={serviceForm.whenNeeded.heading}
                      onChange={(e) => setServiceForm({
                        ...serviceForm,
                        whenNeeded: { ...serviceForm.whenNeeded, heading: e.target.value }
                      })}
                      className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:border-indigo-600"
                    />

                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Add situation / symptom requirement (e.g. An elderly family member needs assistance)..."
                        value={whenNeededItemInput}
                        onChange={(e) => setWhenNeededItemInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddWhenNeededItem(); } }}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-indigo-600"
                      />
                      <button
                        type="button"
                        onClick={handleAddWhenNeededItem}
                        className="px-3.5 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center"
                      >
                        + Add Item
                      </button>
                    </div>

                    {serviceForm.whenNeeded.items?.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {serviceForm.whenNeeded.items.map((it, idx) => (
                          <div key={idx} className="bg-white px-3 py-2 rounded-lg border border-slate-200 text-xs text-slate-700 flex items-center justify-between gap-2 shadow-2xs">
                            <span className="flex items-center gap-1.5 truncate">
                              <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                              <span className="truncate font-medium">{it}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveWhenNeededItem(idx)}
                              className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 3. Care Philosophy & Clinical Insights (e.g. Clinical Excellence / Preserving Dignity) */}
                  <div className="p-4 bg-slate-50/70 rounded-xl border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-teal-600" />
                        3. Care Philosophy & Clinical Insights Card
                      </label>
                      <span className="text-[11px] font-bold text-teal-800 bg-teal-100/70 px-2.5 py-0.5 rounded-full border border-teal-200">
                        {serviceForm.insights?.length || 0} {serviceForm.insights?.length === 1 ? 'Card' : 'Cards'} Configured
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium">
                      Featured highlight card on the website detail page (e.g. <em>"Clinical excellence with compassionate empathy"</em> or <em>"Preserving dignity and emotional joy"</em>).
                    </p>

                    {/* Form to Add New Insight Card */}
                    <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs space-y-2.5">
                      <span className="text-[11px] font-bold text-slate-700 block">Add New Insight Card:</span>
                      <input
                        type="text"
                        placeholder="Card Title (e.g. Clinical excellence with compassionate empathy)"
                        value={newInsightTitle}
                        onChange={(e) => setNewInsightTitle(e.target.value)}
                        className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:border-teal-600 focus:bg-white"
                      />
                      <div className="flex gap-2">
                        <textarea
                          rows="2"
                          placeholder="Card explanation paragraph (e.g. Our registered nurses are trained in strict infection-control protocols, patient safety, and compassionate bedside communication...)..."
                          value={newInsightParagraph}
                          onChange={(e) => setNewInsightParagraph(e.target.value)}
                          className="flex-1 px-3.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:border-teal-600 focus:bg-white resize-none"
                        />
                        <button
                          type="button"
                          onClick={handleAddInsight}
                          className="px-4 bg-teal-700 hover:bg-teal-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center gap-1 shadow-2xs"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>Add Card</span>
                        </button>
                      </div>
                    </div>

                    {/* Existing Configured Insights List */}
                    {serviceForm.insights?.length > 0 && (
                      <div className="space-y-3 pt-1">
                        {serviceForm.insights.map((insight, idx) => (
                          <div key={idx} className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
                            {/* Insight Card Header */}
                            <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-2">
                              <div className="flex-1 flex items-center gap-2">
                                <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                  {idx + 1}
                                </span>
                                <input
                                  type="text"
                                  value={insight.title}
                                  onChange={(e) => handleUpdateInsightTitle(idx, e.target.value)}
                                  placeholder="Card Title (e.g. Clinical excellence with compassionate empathy)"
                                  className="flex-1 px-2.5 py-1 bg-white border border-slate-200 rounded-md text-xs font-black text-slate-900 focus:outline-none focus:border-teal-600"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveInsight(idx)}
                                className="text-slate-400 hover:text-rose-600 p-1 cursor-pointer shrink-0 transition-colors"
                                title="Delete this insight card"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {/* Paragraphs in this Insight */}
                            <div className="p-3.5 space-y-2.5">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                                Card Content Paragraphs ({insight.paragraphs?.length || 0}):
                              </label>
                              {insight.paragraphs?.map((para, pIdx) => (
                                <div key={pIdx} className="flex items-start gap-2 bg-slate-50/80 p-2.5 rounded-lg border border-slate-200/80">
                                  <textarea
                                    rows="2"
                                    value={para}
                                    onChange={(e) => handleUpdateInsightParagraph(idx, pIdx, e.target.value)}
                                    className="flex-1 bg-transparent border-0 text-xs text-slate-800 font-medium resize-none focus:outline-none leading-relaxed"
                                    placeholder="Insight description paragraph..."
                                  />
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveParagraphFromInsight(idx, pIdx)}
                                    className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer shrink-0 mt-0.5"
                                    title="Remove paragraph"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}

                              {/* Add Paragraph to this specific insight */}
                              <div className="flex gap-2 pt-1">
                                <input
                                  type="text"
                                  placeholder="Add another paragraph to this card..."
                                  value={newInsightParaInputs[idx] || ''}
                                  onChange={(e) => setNewInsightParaInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddParagraphToInsight(idx); } }}
                                  className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:border-teal-600 focus:bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleAddParagraphToInsight(idx)}
                                  className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0"
                                >
                                  + Add Paragraph
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 4. Scope Notice Banner */}
                  <div className="p-4 bg-amber-50/70 rounded-xl border border-amber-200 space-y-3">
                    <label className="text-xs font-black uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      4. Important Service Note / Scope Notice Banner
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Notice Title (e.g. Important Service Note)"
                        value={serviceForm.scopeNotice.title}
                        onChange={(e) => setServiceForm({
                          ...serviceForm,
                          scopeNotice: { ...serviceForm.scopeNotice, title: e.target.value }
                        })}
                        className="w-full px-3.5 py-1.5 bg-white border border-amber-200 rounded-lg text-xs font-bold focus:outline-none focus:border-amber-600"
                      />
                      <input
                        type="text"
                        placeholder="Badge Text (e.g. Non-Clinical Care)"
                        value={serviceForm.scopeNotice.badge}
                        onChange={(e) => setServiceForm({
                          ...serviceForm,
                          scopeNotice: { ...serviceForm.scopeNotice, badge: e.target.value }
                        })}
                        className="w-full px-3.5 py-1.5 bg-white border border-amber-200 rounded-lg text-xs font-medium focus:outline-none focus:border-amber-600"
                      />
                    </div>

                    <div className="flex gap-2">
                      <textarea
                        rows="2"
                        placeholder="Add notice disclaimer or scope limitation text..."
                        value={scopeParagraphInput}
                        onChange={(e) => setScopeParagraphInput(e.target.value)}
                        className="flex-1 px-3.5 py-1.5 bg-white border border-amber-200 rounded-xl text-xs font-medium focus:outline-none focus:border-amber-600 resize-none"
                      />
                      <button
                        type="button"
                        onClick={handleAddScopeParagraph}
                        className="px-3.5 bg-amber-700 hover:bg-amber-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer shrink-0 flex items-center justify-center"
                      >
                        + Add
                      </button>
                    </div>

                    {serviceForm.scopeNotice.paragraphs?.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        {serviceForm.scopeNotice.paragraphs.map((p, idx) => (
                          <div key={idx} className="bg-white p-2.5 rounded-lg border border-amber-200/80 text-xs text-amber-950 flex items-start justify-between gap-2">
                            <p className="flex-1 leading-relaxed">{p}</p>
                            <button
                              type="button"
                              onClick={() => handleRemoveScopeParagraph(idx)}
                              className="text-amber-400 hover:text-rose-600 p-0.5 cursor-pointer shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 4. Consultation CTA */}
                  <div className="p-4 bg-sky-50/70 rounded-xl border border-sky-200 space-y-3">
                    <label className="text-xs font-black uppercase tracking-wider text-sky-900 block">
                      5. Bottom Consultation CTA Card
                    </label>
                    <input
                      type="text"
                      placeholder="CTA Heading (e.g. Need Help Choosing the Right Caregiver?)"
                      value={serviceForm.cta.title}
                      onChange={(e) => setServiceForm({
                        ...serviceForm,
                        cta: { ...serviceForm.cta, title: e.target.value }
                      })}
                      className="w-full px-3.5 py-1.5 bg-white border border-sky-200 rounded-lg text-xs font-bold focus:outline-none focus:border-sky-600"
                    />
                    <textarea
                      rows="2"
                      placeholder="CTA Body text explaining why patient should request consultation..."
                      value={serviceForm.cta.text}
                      onChange={(e) => setServiceForm({
                        ...serviceForm,
                        cta: { ...serviceForm.cta, text: e.target.value }
                      })}
                      className="w-full px-3.5 py-2 bg-white border border-sky-200 rounded-lg text-xs font-medium focus:outline-none focus:border-sky-600 resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Modal Footer Controls */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100 shrink-0 sticky bottom-0 bg-white">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-500 font-semibold">
                    Tab: {serviceModalTab === 'basic' ? '1. Basic' : serviceModalTab === 'pricing' ? '2. Pricing' : serviceModalTab === 'content' ? '3. Content' : '4. FAQ'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowServiceModal(false)}
                    className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingService || uploadingServicePhoto}
                    className="px-5 py-2 bg-teal-700 text-white rounded-xl text-xs font-bold hover:bg-teal-800 disabled:opacity-50 flex items-center gap-1.5 cursor-pointer shadow-sm"
                  >
                    {submittingService && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    <span>{editingService ? 'Update OPHE Service' : 'Save OPHE Service'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpheServiceManagement;
